import {
  basePriceFromClientPrice,
  computeClientPriceBreakdown,
  computeGrossPriceBreakdown,
  type ClientPriceBreakdown,
} from "@/lib/client-price";
import { checkServicePrice, type ServicePriceLimits } from "@/lib/service-price-limits-shared";
import type { MoneyConfig } from "@/lib/money/session-money";

/** Cijena bez PDV-a s kojom je forma učitana (ono što polje prikazuje), plus sirovi
 *  bruto kako je spremljen, da se netaknuta cijena spremi byte-identično umjesto da se
 *  ponovo preračuna. */
export interface ServiceSavedBaseline {
  base: number | null;
  gross: number | null;
}

/** Baseline za formu koja jos nista nije ucitala (nova usluga). */
export const EMPTY_SERVICE_BASELINE: ServiceSavedBaseline = { base: null, gross: null };

/**
 * `services.price` je spremljena bruto (sa PDV-om, ono sto klijent placa), a i
 * onboarding korak i stranica "Usluge" puste terapeuta da upise cijenu bez PDV-a, pa se
 * pri ucitavanju konvertuje. Bruto se cuva doslovno uz izvedenu cijenu bez PDV-a, jer
 * bruto -> bez PDV-a -> bruto nije idempotentno za svaki spremljeni bruto: kalkulator
 * sam proizvodi samo bruto vrijednosti iz slike "osnovica + PDV", a cijena upisana
 * drugim putem (WP migracija, admin unos, kasnija promjena stope PDV-a) ne mora biti medju njima.
 * Npr. spremljenih 16.00 se ucita kao 13.68 bez PDV-a, a to nazad daje 16.01. Put
 * spremanja nepromijenjene cijene zato mora ponovo upotrijebiti bas taj sirovi bruto
 * umjesto da ga preracuna.
 */
export function loadServiceSavedBaseline(
  storedGrossPrice: number | null,
  config: MoneyConfig,
): ServiceSavedBaseline {
  return { base: basePriceFromClientPrice(storedGrossPrice, config), gross: storedGrossPrice };
}

/**
 * Bruto cijena koju bi forma spremila za datu unesenu cijenu bez PDV-a. Unos koji je
 * jednak ucitanom baseline-u je NEPROMIJENJEN i vraca originalni spremljeni bruto, pa
 * izmjena samo naziva usluge ne pomjeri cijenu za paru. Bez provjere granica, da se
 * ista vrijednost moze koristiti i za prikaz (hint ispod polja) i za spremanje.
 */
export function serviceSaveGrossPrice(
  enteredBase: number,
  baseline: ServiceSavedBaseline,
  config: MoneyConfig,
): number {
  if (isUnchangedBase(enteredBase, baseline)) return baseline.gross as number;
  return computeClientPriceBreakdown(enteredBase, config).clientPrice;
}

/**
 * Razlaganje (osnovica, provizija, terapeutu, PDV, klijent plaća) bas za cijenu koju bi
 * forma spremila. Netaknuta cijena se razlaže iz sirovog spremljenog bruta, pa kalkulator
 * za spremljenih 16.00 pokaže "Klijent plaća 16.00", a ne 16.01 preračunato iz 13.68.
 */
export function serviceSaveBreakdown(
  enteredBase: number | null,
  baseline: ServiceSavedBaseline,
  config: MoneyConfig,
): ClientPriceBreakdown {
  if (enteredBase !== null && isUnchangedBase(enteredBase, baseline)) {
    return computeGrossPriceBreakdown(baseline.gross, config);
  }
  return computeClientPriceBreakdown(enteredBase, config);
}

function isUnchangedBase(enteredBase: number, baseline: ServiceSavedBaseline): boolean {
  return baseline.base !== null && baseline.gross !== null && enteredBase === baseline.base;
}

export type ServicePriceResolution = { price: number } | { error: string };

/**
 * Odluka koju bruto cijenu spremiti. Nepromijenjena cijena prolazi i kad je van
 * trenutno konfigurisanih granica (grandfathering, isto pravilo koje server primjenjuje
 * u `src/api/services.ts` preko `nextPrice !== service.price`). Bez toga starija cijena
 * van danasnjeg raspona postane trajno nespremljiva, pa se usluzi ne moze promijeniti
 * ni naziv. Svaki drugi unos (cijena bez PDV-a) se provjerava protiv raspona, koji je
 * zadan za cijenu bez PDV-a, i konvertuje u bruto za spremanje.
 */
export function resolveServiceSavePrice(
  enteredBase: number,
  baseline: ServiceSavedBaseline,
  limits: ServicePriceLimits,
  config: MoneyConfig,
): ServicePriceResolution {
  const price = serviceSaveGrossPrice(enteredBase, baseline, config);
  if (baseline.gross !== null && price === baseline.gross) return { price };
  // Raspon je za cijenu bez PDV-a, tj. bas ono sto je terapeut upisao.
  const priceError = checkServicePrice(enteredBase, limits);
  if (priceError) return { error: priceError };
  return { price };
}

/**
 * Terapeut je u polje "bez PDV-a" upisao dosadašnju cijenu za klijenta (sa PDV-om),
 * npr. 81.90 umjesto 70.00. Česta greška kod migriranih terapeuta koji znaju samo
 * cijenu koju klijent plaća; spremljeno bi dodalo PDV još jednom i poskupilo seansu.
 */
export function typedOldClientPriceAsBase(price: number | null, baseline: ServiceSavedBaseline): boolean {
  if (price === null || baseline.gross === null || baseline.base === null) return false;
  if (Math.abs(baseline.gross - baseline.base) < 0.005) return false;
  return Math.abs(price - baseline.gross) < 0.005;
}
